import React, { useState, useEffect, useMemo } from "react";
import Axios from "axios";
import {
  Add,
  AddSquare,
  ArrowLeft,
  ArrowRight2,
  Cross,
  Edit,
  Minus,
  MinusSquare,
  Trash,
  UserProfile,
} from "../CustomComponents/SwiftIcon/Icons";
import SwiftToolTip from "../CustomComponents/SwiftToolTip/SwiftToolTip";
import FamilyStocksTable from "./FamilyStocksTable";
import {
  numberFormat,
  priceFormat,
  titleCase,
} from "../../exports/UtilFunctions";
import { GetCookie } from "../../exports/CookieManagement";
import ConfirmBox from "../CustomComponents/ConfirmBox/ConfirmBox";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert";
import SectorMarketChart from "../Body/MenuSection/PortfolioComponents/SectorMarketChart";
import Pulse from "../Loader/Pulse";
import { COLOR_VARS } from "../../exports/ColorVars";
import FamilyPortfolioTable from "./FamilyPortfolioTable";
import ServerRequest from "../../exports/ServerRequest";
import { GetRoleType } from "../../exports/RoleTypes";
import FamilySipTable from "./FamilySipTable";
import MFPerformanceMetrics from "../MFManagement/MFPerformanceMetrics";
import MFChartContainer from "../MFManagement/MFChartContainer.js";
import CustomSelect from "../Body/CustomChartComponents/CustomSelect/CustomSelect.js";
import SwiftModal from "../CustomComponents/SwiftModal/SwiftModal.js";
import { getDeviceSize } from "../../exports/InteractiveUtils.js";
import Delete from "../../assets/icons/delete_24.svg";
import DeleteBlack from "../../assets/icons/delete_24black.svg";

const error_config = {
  Band: true,
  BandColor: "var(--main-blue)",
  BoxColor: "var(--white)",
  TextColor: "var(--text-color)",
  AutoClose: {
    Active: true,
    Line: true,
    LineColor: "var(--main-blue)",
    Time: 3,
  },
};

const DEVICE_SIZE = getDeviceSize();

const REQUEST_BASE_URL = process.env.REACT_APP_REQUEST_BASE_URL;

function FamilyPortfolioDetails({
  portfolio,
  setRenameOpen,
  setCustomerOpen,
  update = () => {},
}) {
  const {
    family_portfolio_id,
    family_portfolio_name,
    family_portfolio_include_self,
    family_portfolio_customers,
  } = portfolio;

  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState({});
  const [sectorData, setSectorData] = useState(false);
  const [portfolioDetails, setPortfolioDetails] = useState(false);
  const [portfolioFlag, setPortfolioFlag] = useState(false);
  const [portfolioData, setPortfolioData] = useState(false);
  const [transaction, setTransaction] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [includeSelfAccount, setIncludeSelfAccount] = useState(null);
  const [sipDetails, setSipDetails] = useState([]);
  const [sipLoading, setSipLoading] = useState(true);
  const [sipTotalActiveAmount, setSipTotalActiveAmount] = useState(0);
  const [chartPopup, setChartPopup] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  const [benchmarkData, setBenchmarkData] = useState([]);
  const [selectedBenchmarkObj, setSelectedBenchmarkObj] = useState(null);
  const [selectedBenchmark, setSelectedBenchmark] = useState("Nifty_50");
  const [benchmarkPerformance, setBenchmarkPerformance] = useState({});
  const [navPerformance, setNavPerformance] = useState({});

  const [detailsLoading, setDetailsLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [chartLoadingMsg, setChartLoadingMsg] = useState("Loading chart data");
  const [chartOpacityMsg, setChartOpacityMsg] = useState(true);

  const isSmallDevice = DEVICE_SIZE === "S";
  const user_role_type = GetRoleType(GetCookie("role"));
  const account_token = GetCookie("token");

  const portfolio_account_codes = family_portfolio_customers.map(
    (item) => item.customer_account_code,
  );
  // const displayCustomer = selectedCustomers.length > 0 ? [...family_portfolio_customers, clientName] : family_portfolio_customers

  const displayCustomer = useMemo(() => {
    return includeSelfAccount
      ? [...family_portfolio_customers, clientName]
      : family_portfolio_customers;
  }, [family_portfolio_customers, clientName, includeSelfAccount]);

  useEffect(() => {
    setSectorData(false);
    setPortfolioDetails(false);
    setPortfolioData(false);
    GetPortfolioData();
    GetSectorData();
    fetchTransactionWithNAV();
    fetchSipDetails();
    GetClientInfo();
  }, [
    family_portfolio_id,
    portfolioFlag,
    includeSelfAccount,
    selectedCustomers,
  ]);

  const isDefined = (...flags) => flags.every((v) => v !== undefined);

  useEffect(() => {
    if (!isDefined(chartLoading, metricsLoading)) return;

    const allLoaded = !chartLoading && !metricsLoading;
    setDetailsLoading(!allLoaded);
  }, [chartLoading, metricsLoading]);

  useEffect(() => {
    getNavPerformanceMetric();
    getBenchmarkPerformanceMetric();
  }, [account_token]);

  useEffect(() => {
    setIncludeSelfAccount(family_portfolio_include_self ? true : false);
  }, [family_portfolio_include_self]);

  const memoizedTransaction = useMemo(() => transaction, [transaction]);

  function GetPortfolioData() {
    if (includeSelfAccount === null) return;

    setLoading(true);
    const account_token = GetCookie("token");

    Axios.get(
      selectedCustomers.length > 0
        ? `${REQUEST_BASE_URL}/family/portfolio?account_codes=${selectedCustomers.toString()}&include_self_account=${includeSelfAccount}&token=${account_token}`
        : `${REQUEST_BASE_URL}/family/portfolio?portfolio_id=${family_portfolio_id}&include_self_account=${includeSelfAccount}&token=${account_token}`,
    )
      .then((response) => {
        const data = response.data ? response.data : response;
        if (!data.error) {
          const { portfolioData, sum } = data.portfolio || data;
          setPortfolioDetails({ ...sum, originalValue: sum.lastValueSum });
          setPortfolioData(portfolioData);
          setLoading(false);
        } else {
          setPortfolioDetails(false);
          setPortfolioData(false);
          setLoading(false);
        }
      })
      .catch(() => {
        setPortfolioDetails(false);
        setPortfolioData(false);
        setLoading(false);
      });
  }

  const handleCustomerClick = (customer) => {
    setSelectedCustomers((prev) => {
      if (prev.includes(customer.customer_account_code)) {
        // unselect
        return prev.filter((c) => c !== customer.customer_account_code);
      } else {
        // select
        return [...prev, customer.customer_account_code];
      }
    });
  };

  async function GetClientInfo() {
    const role = user_role_type === "CUSTOMER" ? "customer" : "superuser";
    const response = await fetch(
      `${REQUEST_BASE_URL}/clientinfo/${role}?token=${account_token}`,
    );
    const data = await response.json();
    console.log(data);
    if (data.error)
      throw new Error(data.message || "Failed to fetch client info");
    setClientName((prev) => ({
      ...prev,
      customer_account_code: data.account_data.account_code,
      customer_name: data.account_data.name,
    }));
  }

  const mutualFundValue = parseFloat(
    transaction[transaction.length - 1]?.investment_value ?? 0,
  );
  const equityValue = parseFloat(portfolioDetails?.portfolioValue);

  const total = mutualFundValue + equityValue;

  const equityVsMfTable =
    total > 0
      ? [
          {
            name: "Mutual Funds",
            value: ((mutualFundValue / total) * 100).toFixed(2),
            volume: `${mutualFundValue.toFixed(2)}`,
          },
          {
            name: "Equity",
            value: ((equityValue / total) * 100).toFixed(2),
            volume: `${equityValue.toFixed(2)}`,
          },
        ]
      : [];

  function GetSectorData() {
    const account_token = GetCookie("token");

    const url =
      selectedCustomers.length > 0
        ? `${REQUEST_BASE_URL}/family/sector?account_codes=${selectedCustomers.toString()}&include_self_account=${includeSelfAccount}&token=${account_token}`
        : `${REQUEST_BASE_URL}/family/sector?portfolio_id=${family_portfolio_id}&include_self_account=${includeSelfAccount}&token=${account_token}`;

    Axios.get(url)
      .then((response) => {
        const sectorData = response?.data?.data?.sector
          ? response?.data?.data?.sector
          : response?.data?.data;

        setSectorData(sectorData);
        console.log("Sector data:", sectorData);
      })
      .catch((err) => {
        console.error("Failed to fetch sector data:", err);
        setSectorData([]);
      });
  }

  useEffect(() => {
    const fetchBenchmarkData = async () => {
      if (!account_token && !portfolio_account_codes) return;

      try {
        const url = portfolio_account_codes
          ? `/mf/benchmark/get?account_code=${portfolio_account_codes}` // Superuser
          : `/mf/benchmark/get?token=${account_token}`; // Customer

        const res = await ServerRequest({ url, method: "get" });

        if (!res.error && res.data) {
          setBenchmarkData(res.data);
        } else {
          console.warn("Benchmark fetch failed", res.message);
        }
      } catch (error) {
        console.error("Error fetching benchmark data:", error);
      }
    };

    fetchBenchmarkData();
  }, [selectedBenchmark]);

  const getBenchmarkPerformanceMetric = async () => {
    if (!selectedBenchmark) return;
    try {
      const response = await ServerRequest({
        url: `/mf/benchmark/get/${selectedBenchmark}`,
        method: "get",
      });
      if (!response.error && response.data?.returnStats) {
        setBenchmarkPerformance(response.data.returnStats);
      } else {
        console.warn("BenchMark Performance fetch failed", response.message);
      }
    } catch (error) {
      console.error("Error fetching BenchMark Performance:", error);
    }
  };

  const getNavPerformanceMetric = async () => {
    // Exit only if BOTH are missing
    if (!portfolio_account_codes) return;

    try {
      const url =
        selectedCustomers.length > 0
          ? `/mf/portdata?token=${selectedCustomers}`
          : `/mf/portdata?token=${portfolio_account_codes}`;

      const response = await ServerRequest({ url, method: "get" });

      if (!response.error && response.data) {
        setNavPerformance(response.data.returnStats);
      } else {
        console.warn(
          "NAV Performance fetch failed",
          response.message || "No returnStats in response",
        );
      }
    } catch (error) {
      console.error("Error fetching NAV Performance:", error);
    }
  };

  const fetchTransactionWithNAV = async () => {
    setTransactionLoading(true);
    const account_token = GetCookie("token");
    try {
      const params = new URLSearchParams();
      selectedCustomers.length > 0
        ? selectedCustomers.forEach((code) =>
            params.append("portfolioCode", code),
          )
        : portfolio_account_codes.forEach((code) =>
            params.append("portfolioCode", code),
          );

      const response = await ServerRequest({
        url: `/mf/portfoliomf?${params.toString()}&include_self_account=${includeSelfAccount}&token=${account_token}`,
        method: "get",
      });

      const portfolioData = response?.data;

      // Step 2: Separate rows
      const rowsWithISIN = portfolioData.filter((item) => item.isin);
      const footerRow = portfolioData.find((item) => !item.isin);

      // Step 3: Enrich with NAV change
      const enrichedRows = await Promise.all(
        rowsWithISIN.map(async (item) => {
          const navUrl = `/mf/latestnav/${item.isin}`;
          try {
            const navResp = await ServerRequest({ url: navUrl, method: "get" });
            return { ...item, navChange: navResp?.result?.navChange ?? null };
          } catch {
            return { ...item, navChange: null };
          }
        }),
      );

      const finalData = footerRow ? [...enrichedRows, footerRow] : enrichedRows;
      setTransaction(finalData);
    } catch (err) {
      console.error("Error fetching portfolio/NAV data:", err);
      setTransaction([]);
    }
    setTransactionLoading(false);
  };

  const fetchSipDetails = async () => {
    setSipLoading(true);
    const account_token = GetCookie("token");

    try {
      const params = new URLSearchParams();
      portfolio_account_codes.forEach((code) =>
        params.append("portfolioCode", code),
      );

      // API call (similar to portfolio fetch)
      const response = await ServerRequest({
        url:
          selectedCustomers.length > 0
            ? `/mf/sipmf?${selectedCustomers.toString()}&include_self_account=${includeSelfAccount}&token=${account_token}`
            : `/mf/sipmf?${params.toString()}&include_self_account=${includeSelfAccount}&token=${account_token}`,
        method: "get",
      });

      // Expected structure: { results: [], totalActiveAmount: number }
      if (Array.isArray(response?.results)) {
        setSipDetails(response.results);
        setSipTotalActiveAmount(response.totalActiveAmount || 0);
      } else {
        setSipDetails([]);
        setSipTotalActiveAmount(0);
      }
    } catch (err) {
      console.error("Error fetching SIP details:", err);
      setSipDetails([]);
      setSipTotalActiveAmount(0);
    } finally {
      setSipLoading(false);
    }
  };

  function DeletFamilyPortfolio(callback) {
    const account_token = GetCookie("token");

    Axios.delete(
      `${REQUEST_BASE_URL}/family?portfolio_id=${family_portfolio_id}&token=${account_token}`,
    )
      .then((response) => {
        const data = response.data;
        if (!data.error) {
          Alert({
            TitleText: "Family Portfolio Deleted",
            Message: "Portfolio deleted successfully",
            Band: true,
            BandColor: "var(--green-color)",
            BoxColor: "var(--white)",
            TextColor: "var(--text-color)",
            AutoClose: {
              Active: true,
              Line: true,
              LineColor: "var(--green-color)",
              Time: 3,
            },
          });
          update(true);
          callback();
        } else {
          Alert({
            TitleText: "Warning",
            Message: "Something went wrong while deleting the portfolio",
            ...error_config,
          });
          callback();
        }
      })
      .catch(() => {
        Alert({
          TitleText: "Warning",
          Message: "Something went wrong while deleting the portfolio",
          ...error_config,
        });
        callback();
      });
  }

  function DeleteFamilyCustomer(portfolio_customer, callback) {
    if (!portfolio_customer) {
      callback();
      return;
    }

    const account_token = GetCookie("token");

    Axios.post(
      `${REQUEST_BASE_URL}/family/deletecustomer?token=${account_token}`,
      {
        portfolio_id: family_portfolio_id,
        portfolio_customer: portfolio_customer,
      },
    )
      .then((response) => {
        const data = response.data;
        if (!data.error) {
          Alert({
            TitleText: "Customer Removed",
            Message: "Customer successfully removed from portfolio",
            Band: true,
            BandColor: "var(--green-color)",
            BoxColor: "var(--white)",
            TextColor: "var(--text-color)",
            AutoClose: {
              Active: true,
              Line: true,
              LineColor: "var(--green-color)",
              Time: 3,
            },
          });
          setPortfolioFlag(!portfolioFlag);
          callback();
          update(true);
        } else {
          Alert({
            TitleText: "Warning",
            Message: "Something went wrong while removing the customer",
            ...error_config,
          });
          callback();
        }
      })
      .catch(() => {
        Alert({
          TitleText: "Warning",
          Message: "Something went wrong while removing the customer",
          ...error_config,
        });
        callback();
      });
  }

  function SetIndividualPrice(index, value) {
    if (value && value !== undefined && index < portfolioData.length) {
      const data = portfolioData;
      data[index]["CurrentPrice"] = value;
      setPortfolioData([...data]);
    }
  }

  function CalculateReturns() {
    if (!portfolioData || portfolioData.length === 0 || !portfolioDetails)
      return;

    const { costValueSum, macCash, originalValue } = portfolioDetails;

    let current_value_sum = 0;
    portfolioData.forEach((p) => {
      current_value_sum += p["Quantity"] * p["CurrentPrice"];
    });

    const final_portfolio_value = current_value_sum + parseFloat(macCash);

    const profit_loss = final_portfolio_value - costValueSum;
    const current_returns = (profit_loss * 100) / costValueSum;

    const today_profit_loss = current_value_sum - parseFloat(originalValue);
    const today_returns = parseFloat(
      (100 * today_profit_loss) / current_value_sum,
    );

    setPortfolioDetails({
      ...portfolioDetails,
      portfolioValue: final_portfolio_value,
      profitLoss: profit_loss,
      totalReturnSum: current_returns,
      todayProfitLoss: today_profit_loss,
      todayReturns: today_returns,
    });
  }

  useEffect(() => {
    CalculateReturns();
  }, [portfolioData]);

  const {
    costValueSum = 0,
    portfolioValue = 0,
    totalReturnSum = 0,
    profitLoss = 0,
    todayProfitLoss = 0,
    todayReturns = 0,
  } = portfolioDetails;

  const color_class = parseFloat(profitLoss) < 0 ? "negative" : "positive";
  const today_color_class =
    parseFloat(todayProfitLoss) < 0 ? "negative" : "positive";

  return (
    <div className="family-portfolio-details">
      {loading || transactionLoading || sipLoading ? (
        <>
          <div className="family-portfolio-loading">
            <Pulse />
            <p>Loading family portfolio details...</p>
          </div>
        </>
      ) : (
        <>
          <div className="family-details-section">
            <div className="family-name-options">
              <div>
                <p className="family-name">{family_portfolio_name}</p>
              </div>
              <div className="family-options">
                <div
                  className="family-button"
                  onClick={() => setCustomerOpen(true)}
                >
                  <Add color={COLOR_VARS["SWIFT_COLOR8"]} />
                  <p>Add</p>
                </div>
                <div
                  className="family-button"
                  onClick={() => setRenameOpen(true)}
                >
                  <Edit color={COLOR_VARS["SWIFT_COLOR8"]} />
                  <p>Rename</p>
                </div>
                <div
                  className="family-button"
                  onClick={() => {
                    ConfirmBox({
                      title: "Delete Family Portfolio",
                      description: (
                        <>
                          Are you sure you want to delete the portfolio named{" "}
                          <strong>{family_portfolio_name}</strong>?<br /> Keep
                          in mind that by doing so, all customers within the
                          portfolio will be removed.
                        </>
                      ),
                      properties: [
                        {
                          id: "2",
                          label: "Delete",
                          color: "#192b40",
                          bgColor: "var(--base-200)",
                          handleFunction: DeletFamilyPortfolio,
                        },
                      ],
                      cancel: true,
                    });
                  }}
                >
                  <img src={Delete} alt=" " />
                  <p>Delete</p>
                </div>
              </div>
            </div>
            {family_portfolio_customers.length <= 0 ? (
              <>
                <div className="family-portfolio-customers-empty">
                  <UserProfile size={60} color={COLOR_VARS["SWIFT_COLOR4"]} />
                  <p>You don't have any customers in this portfolio</p>
                  <button
                    onClick={() => {
                      setCustomerOpen(true);
                    }}
                  >
                    Add New Customer
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="family-portfolio-customers">
                  {/* <p className='family-data-title'>Added Customers ({family_portfolio_customers.length})</p> */}
                  <div className="customers-wrapper">
                    {displayCustomer.length === 0 ? (
                      <>
                        <></>
                      </>
                    ) : (
                      <>
                        {displayCustomer?.map((c, index) => {
                          const isSelected = selectedCustomers.includes(
                            c.customer_account_code,
                          );

                          return (
                            <div
                              key={index}
                              className={`customer-box ${isSelected ? "selected" : ""}`}
                            >
                              <p
                                className={`customer-name`}
                                onClick={() => handleCustomerClick(c)}
                              >
                                {titleCase(c.customer_name)}
                              </p>
                              <SwiftToolTip
                                title="Remove Customer"
                                placement="bottom"
                              >
                                <div
                                  className="customer-remove"
                                  onClick={() => {
                                    ConfirmBox({
                                      title: "Remove Portfolio Customer",
                                      description: (
                                        <>
                                          Are you sure you want to remove the
                                          customer named{" "}
                                          <strong>
                                            {titleCase(c.customer_name)}
                                          </strong>
                                          ? <br />
                                          This action cannot be undone and all
                                          associated data will be permanently
                                          removed.
                                        </>
                                      ),
                                      properties: [
                                        {
                                          id: "2",
                                          label: "Remove",
                                          color: "#192b40",
                                          bgColor: "#ffffff",
                                          handleFunction: DeleteFamilyCustomer,
                                          functionArgs: [
                                            c.customer_account_code,
                                          ],
                                        },
                                      ],
                                      cancel: true,
                                    });
                                  }}
                                >
                                  <Trash
                                    size={18}
                                    color={COLOR_VARS["SWIFT_COLOR4"]}
                                  />
                                </div>
                              </SwiftToolTip>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
                {user_role_type === "CUSTOMER" && (
                  <div className="family-portfolio-include">
                    <input
                      type="checkbox"
                      checked={includeSelfAccount}
                      onChange={(e) => {
                        setIncludeSelfAccount(e.target.checked);
                      }}
                    />
                    <p className="family-data-title">Include Self Account</p>
                  </div>
                )}
                <div className="family-portfolio-total-portfolio-card">
                  <p className="family-details-title">Total</p>
                  <div className="family-portfolio-config">
                    <div className="total-family-config-box">
                      <p className="family-data-title">
                        Total Investment Value
                      </p>
                      <span>
                        {priceFormat(
                          parseFloat(costValueSum || 0) +
                            parseFloat(
                              transaction[transaction.length - 1]
                                ?.investment_cost || 0,
                            ),
                        )}
                      </span>
                    </div>
                    <div className="family-config-box">
                      <p className="family-data-title">Total Portfolio Value</p>
                      <span>
                        {priceFormat(
                          parseFloat(portfolioValue || 0) +
                            parseFloat(
                              transaction[transaction.length - 1]
                                ?.investment_value || 0,
                            ),
                        )}
                      </span>
                    </div>
                  </div>
                </div>
                {/* ✅ Stocks Section (show only if stocks data exists) */}
                {portfolioData?.length > 0 && portfolioDetails && (
                  <div>
                    <div className="family-portfolio-total-portfolio-card-stocks">
                      <p className="family-details-title">Stocks</p>
                      <div className="family-portfolio-config">
                        <div className="total-family-config-box">
                          <p className="family-data-title">
                            Total Investment Value
                          </p>
                          <span>{priceFormat(costValueSum)}</span>
                        </div>
                        <div className="family-config-box">
                          <p className="family-data-title">
                            Total Portfolio Value
                          </p>
                          <span>{priceFormat(portfolioValue)}</span>
                        </div>
                        <div className="family-config-box">
                          <p className="family-data-title">
                            Total Portfolio Returns
                          </p>
                          <span className={color_class}>
                            {priceFormat(profitLoss)} (
                            {numberFormat(totalReturnSum)}%)
                          </span>
                        </div>
                        <div className="family-config-box">
                          <p className="family-data-title">
                            Portfolio Returns Today
                          </p>
                          <span className={today_color_class}>
                            {priceFormat(todayProfitLoss)} (
                            {numberFormat(todayReturns)}%)
                          </span>
                        </div>
                        <div className="family-config-box">
                          <p className="family-data-title">
                            Total Cash Position
                          </p>
                          <span>
                            {priceFormat(portfolioDetails?.cashValue)} (
                            {priceFormat(portfolioDetails?.cashValueVirtual)})
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="family-portfolio-stocks">
                      <FamilyStocksTable
                        portfolio={portfolioData}
                        SetIndividualPrice={SetIndividualPrice}
                      />
                    </div>
                  </div>
                )}

                {/* ✅ Mutual Funds Section (show only if transaction data exists) */}
                {transaction?.length > 0 && (
                  <div>
                    <div className="family-portfolio-total-portfolio-card-mf">
                      <p className="family-details-title">Mutual Funds</p>
                      <div className="family-portfolio-config">
                        <div className="total-family-config-box">
                          <p className="family-data-title">
                            Total Investment Value
                          </p>
                          <span>
                            {priceFormat(
                              transaction[transaction.length - 1]
                                ?.investment_cost,
                            )}
                          </span>
                        </div>
                        <div className="family-config-box">
                          <p className="family-data-title">
                            Total Portfolio Value
                          </p>
                          <span>
                            {priceFormat(
                              transaction[transaction.length - 1]
                                ?.investment_value,
                            )}
                          </span>
                        </div>
                        <div className="family-config-box">
                          <p className="family-data-title">
                            Total Portfolio Returns
                          </p>
                          <span className={color_class}>
                            {priceFormat(
                              transaction[transaction.length - 1]?.profit_loss,
                            )}{" "}
                            (
                            {numberFormat(
                              transaction[transaction.length - 1]?.total_return,
                            )}
                            %)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="family-portfolio-stocks">
                      <FamilyPortfolioTable transaction={memoizedTransaction} />
                    </div>
                    <div className="report-section-button">
                      <div
                        className="swift-button-icon"
                        onClick={() => setChartPopup(true)}
                      >
                        <ArrowRight2 color={"#ffffff"} />
                        <p>Portfolio Performance</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ✅ SIP Section (show only if SIP data exists) */}
                {(sipDetails?.length > 0 || sipTotalActiveAmount > 0) && (
                  <div>
                    <p className="family-details-title">SIPs</p>
                    <div className="family-portfolio-config">
                      <div className="family-config-box">
                        <p className="family-data-title">Total Amount</p>
                        <span>{priceFormat(sipTotalActiveAmount)}</span>
                      </div>
                    </div>
                    <div className="family-portfolio-stocks">
                      <FamilySipTable sipDetails={sipDetails} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div></div>
          <div>
            {family_portfolio_customers.length > 0 && sectorData && (
              <>
                <div className="family-portfolio-charts">
                  {sectorData.industryTable && (
                    <div className="family-sector">
                      {sectorData.industryTable ? (
                        <>
                          <p className="family-details-title">Sector Weights</p>
                          <SectorMarketChart
                            values={sectorData["industryTable"]}
                            unit="Inv. Value Rs."
                          />
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div>
            {family_portfolio_customers.length > 0 && equityVsMfTable && (
              <>
                <div className="family-portfolio-charts">
                  {equityValue && (<div className="family-sector">
                    {equityValue ? (
                      <>
                        <p className="family-details-title">Allocations</p>
                        <SectorMarketChart
                          values={equityVsMfTable}
                          unit="PortFolio Value Rs."
                        />
                      </>
                    ) : (
                      <></>
                    )}
                  </div>)}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {chartPopup && (
        <SwiftModal
          closeModal={() => {
            setChartPopup(false);
          }}
          top="2%"
        >
          <div className="swift-mf-details-popup swift-mf-popup mf-container ">
            {detailsLoading && (
              <div className="app__login__screen">
                <div className="app__login__process">
                  <Pulse />
                  {/* <p className={chartOpacityMsg ? "fadeIn" : "fadeOut"}>
                    {chartLoadingMsg}
                  </p> */}
                </div>
              </div>
            )}
            <>
              {!detailsLoading && (
                <div className="swift-mf-popup-header">
                  <div
                    className="swift-account-svg"
                    onClick={() => {
                      setChartPopup(false);
                    }}
                  >
                    {isSmallDevice ? (
                      <ArrowLeft size={30} color={COLOR_VARS["SWIFT_COLOR4"]} />
                    ) : (
                      ""
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <p className="swift-mf-popup-name">
                      Composite Portfolio Performance
                      <br />
                      <span style={{ fontSize: "12px", fontWeight: "500" }}>
                        Simulated NAV of your portfolio taking into account your
                        inflows and outflows.
                      </span>
                    </p>
                    {isSmallDevice ? (
                      ""
                    ) : (
                      <div
                        className="swift-account-svg"
                        onClick={() => {
                          setChartPopup(false);
                        }}
                      >
                        <Cross size={30} color={COLOR_VARS["SWIFT_COLOR4"]} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="swift-mf-details-popup-body swift-mf-details-popup-body-flex">
                <>
                  {!detailsLoading && (
                    <div className="swift-mf-details-body-head">
                      <div className="swift-mf-head-left"></div>

                      <CustomSelect
                        defaultIndex={Math.max(
                          0,
                          benchmarkData.findIndex(
                            (b) =>
                              b.benchmark_common_name ===
                              selectedBenchmarkObj?.benchmark_common_name,
                          ),
                        )}
                        options={benchmarkData.map(
                          (b) => b.benchmark_common_name,
                        )}
                        width={240}
                        height={40}
                        placeholder="Select Benchmark"
                        onTypeChange={(value) => {
                          const selected = benchmarkData.find(
                            (b) => b.benchmark_common_name === value,
                          );
                          setSelectedBenchmarkObj(selected);
                          setSelectedBenchmark(
                            selected?.benchmark_database_name,
                          );
                        }}
                      />
                    </div>
                  )}
                  <div className="swift-mf-chart-section full-chart-wrapper">
                    <div className="chart-section-inner">
                      {isSmallDevice == "S" ? (
                        <MFChartContainer
                          selectedData={
                            portfolio_account_codes
                              ? portfolio_account_codes
                              : account_token
                          }
                          // const isSuperuser={!!account_code && customer}
                          chartHeight={200}
                          chartWidth={1040}
                          selectedBenchmark={selectedBenchmark}
                          onChangeLoading={setChartLoading}
                          showContent={!detailsLoading}
                          chartType={"portfolio"}
                        />
                      ) : (
                        <MFChartContainer
                          selectedData={
                            portfolio_account_codes
                              ? portfolio_account_codes
                              : account_token
                          }
                          // const isSuperuser={account_code && customer}
                          chartHeight={300}
                          chartWidth={1040}
                          selectedBenchmark={selectedBenchmark}
                          onChangeLoading={setChartLoading}
                          showContent={!detailsLoading}
                          chartType={"portfolio"}
                        />
                      )}
                    </div>
                  </div>
                  <div className="swift-mf-chart-performancemetric">
                    <MFPerformanceMetrics
                      navPerformance={navPerformance}
                      benchmarkPerformance={benchmarkPerformance}
                      onChangeLoading={setMetricsLoading}
                      showContent={!detailsLoading}
                    />
                  </div>
                </>
              </div>
            </>
          </div>
        </SwiftModal>
      )}
    </div>
  );
}

export default FamilyPortfolioDetails;
